import { createContext, ReactNode, useContext, VFC } from 'react'
import { ToastContainer } from 'src/components/Toast/ToastContainer'
import { useToast } from 'src/hooks/useToast'

type ToastContextType = {
  showToast: (
    message: string,
    type?: 'success' | 'error' | 'info',
    duration?: number,
  ) => string
  showSuccess: (message: string, duration?: number) => string
  showError: (message: string, duration?: number) => string
  showInfo: (message: string, duration?: number) => string
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToastContext = () => {
  const context = useContext(ToastContext)
  if (context === undefined)
    throw new Error('useToastContext must be used within a ToastProvider')

  return context
}

type ToastProviderProps = {
  children: ReactNode
}

const ToastProviderComponent: VFC<ToastProviderProps> = ({ children }) => {
  const { toasts, showToast, showSuccess, showError, showInfo, removeToast } =
    useToast()

  const value: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showInfo,
    removeToast,
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </ToastContext.Provider>
  )
}

export const ToastProvider = ToastProviderComponent
